import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';

const NavItem = ({ title, id, items, colorInvert = false }) => {
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState(null);
    const [openedPopoverId, setOpenedPopoverId] = useState(null);

    const handleClick = (event, popoverId) => {
        setAnchorEl(event.currentTarget);
        setOpenedPopoverId(popoverId);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setOpenedPopoverId(null);
    };

    const [activeLink, setActiveLink] = useState('');
    useEffect(() => {
        setActiveLink(
            window && window.location ? window.location.pathname : '',
        );
    }, []);

    const hasActiveLink = () => items.find((i) => i.href === activeLink);
    const isOpen = openedPopoverId === id;
    const linkColor = isOpen || hasActiveLink() ? 'primary.main' : colorInvert ? 'common.white' : 'text.primary';

    return (
        <Box>
            <Box
                display={'flex'}
                alignItems={'center'}
                aria-describedby={id}
                sx={{ cursor: 'pointer' }}
                onClick={(e) => handleClick(e, id)}
            >
                <Typography
                    fontWeight={isOpen || hasActiveLink() ? 700 : 400}
                    color={linkColor}
                    sx={{ transition: 'color 120ms ease' }}
                >
                    {title}
                </Typography>
                <ExpandMoreIcon
                    sx={{
                        marginLeft: theme.spacing(1 / 4),
                        width: 16,
                        height: 16,
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        color: linkColor,
                    }}
                />
            </Box>
            <Popover
                elevation={6}
                id={id}
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                sx={{
                    '.MuiPaper-root': {
                        maxWidth: items.length > 12 ? 350 : 250,
                        padding: 1.5,
                        marginTop: 1.5,
                        borderRadius: 2,
                        borderTop: `2px solid ${theme.palette.primary.main}`,
                        boxShadow: theme.shadows[6],
                        backgroundImage: 'none',
                    },
                }}
            >
                <Grid container spacing={0.25}>
                    {items.map((p, i) => (
                        <Grid item key={i} xs={items.length > 12 ? 6 : 12}>
                            {p.title === 'International Guidelines1' ? (
                                <a
                                    href={p.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Button
                                        fullWidth
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textAlign: 'left',
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 1.5,
                                            gap: 1,
                                            color:
                                                activeLink === p.href
                                                    ? theme.palette.primary.main
                                                    : theme.palette.text.primary,
                                            backgroundColor:
                                                activeLink === p.href
                                                    ? alpha(theme.palette.primary.main, 0.12)
                                                    : 'transparent',
                                            border:
                                                activeLink === p.href
                                                    ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                                    : '1px solid transparent',
                                            fontWeight:
                                                activeLink === p.href ? 600 : 400,
                                            transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            },
                                        }}
                                    >
                                        {activeLink === p.href && (
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                        )}
                                        {p.title}
                                        {p.isNew && (
                                            <Box
                                                padding={0.5}
                                                display={'inline-flex'}
                                                borderRadius={1}
                                                bgcolor={'primary.main'}
                                                marginLeft={2}
                                            >
                                                <Typography
                                                    variant={'caption'}
                                                    sx={{
                                                        color: 'common.white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    new
                                                </Typography>
                                            </Box>
                                        )}
                                    </Button>
                                </a>
                            ) : (
                                <Link
                                    to={p.href}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Button
                                        fullWidth
                                        onClick={handleClose}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textAlign: 'left',
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 1.5,
                                            gap: 1,
                                            color:
                                                activeLink === p.href
                                                    ? theme.palette.primary.main
                                                    : theme.palette.text.primary,
                                            backgroundColor:
                                                activeLink === p.href
                                                    ? alpha(theme.palette.primary.main, 0.12)
                                                    : 'transparent',
                                            border:
                                                activeLink === p.href
                                                    ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                                    : '1px solid transparent',
                                            fontWeight:
                                                activeLink === p.href ? 600 : 400,
                                            transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            },
                                        }}
                                    >
                                        {activeLink === p.href && (
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                        )}
                                        {p.title}
                                        {p.isNew && (
                                            <Box
                                                padding={0.5}
                                                display={'inline-flex'}
                                                borderRadius={1}
                                                bgcolor={'primary.main'}
                                                marginLeft={2}
                                            >
                                                <Typography
                                                    variant={'caption'}
                                                    sx={{
                                                        color: 'common.white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    new
                                                </Typography>
                                            </Box>
                                        )}
                                    </Button>
                                </Link>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Popover>
        </Box>
    );
};

NavItem.propTypes = {
    title: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    colorInvert: PropTypes.bool,
};

export default NavItem;
